import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SEO, PAGE_SEO } from "@/components/SEO";

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

export default function OurStory() {
  return (
    <div className="pt-24">
      <SEO {...PAGE_SEO.ourStory} />
      {/* Hero banner */}
      <div className="relative h-[260px] md:h-[340px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1666739651802-1d8456958a6c?auto=format&fit=crop&w=1920&q=80"
          alt="Calm ocean waves"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
      </div>

      <section className="-mt-24 relative z-10 py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <Breadcrumb className="mb-8">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Our Story</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
            >
              Why we built this
            </motion.p>
            <motion.h1
              variants={fadeInUp}
              className="font-serif text-4xl md:text-5xl text-foreground leading-tight"
            >
              Our Story
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="mt-5 text-lg text-muted-foreground leading-relaxed"
            >
              Lifely was born from a deeply personal experience — the kind
              nobody plans for but everyone eventually faces.
            </motion.p>
          </motion.div>

          {/* The story */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-16 space-y-12"
          >
            {/* Opening */}
            <div className="space-y-6">
              <p className="text-muted-foreground leading-[1.9] text-lg">
                After the sudden passing of my father-in-law, my partner and I found ourselves trying to navigate the process of settling someone's affairs without a will — and without knowing what existed, where it was, or how to access it.
              </p>
              <p className="text-foreground leading-[1.9] text-lg font-medium italic">
                Nothing prepares you for that.
              </p>
            </div>

            {/* The situation */}
            <div className="space-y-6">
              <p className="text-muted-foreground leading-[1.9]">
                We were living in another country at the time. We had to travel back to Brazil and deal with unfamiliar legal processes, different standards, and a system we didn't understand. What should have been a period of reflection and closure became a prolonged exercise in uncertainty.
              </p>
              <div className="bg-secondary/50 rounded-xl p-6 border-l-2 border-primary/40">
                <p className="text-foreground leading-[1.9] mb-2">We didn't know whether a will existed.</p>
                <p className="text-muted-foreground leading-[1.9]">If one did exist, we didn't know where it was.</p>
              </div>
            </div>

            {/* The unknowns */}
            <div className="space-y-6">
              <p className="text-muted-foreground leading-[1.9]">
                We knew he owned the home he lived in — but we didn't know if there were other properties. He had previously owned land in Europe with family members. It was unclear whether those arrangements still existed. Some information was vague. Some conversations were avoided. Some questions had no answers.
              </p>
              <p className="text-muted-foreground leading-[1.9]">
                He worked for an international company and was paid into European bank accounts. We didn't know which bank. We didn't know how many accounts. We didn't even know which country to start with.
              </p>
            </div>

            {/* Bitcoin section */}
            <div className="bg-secondary/40 rounded-2xl p-8 md:p-10 border border-border/30">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Heart className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    And then there was Bitcoin.
                  </h2>
                </div>
              </div>
              <div className="space-y-6">
                <p className="text-muted-foreground leading-[1.9]">
                  He had purchased Bitcoin in the very early days. It was self-custodied — stored somewhere on a hard drive. We never found it. We never learned how much there was. With self-custody, there is no institution to call, no password reset desk, no administrator who can step in.
                </p>
                <p className="text-foreground leading-[1.9] font-medium italic border-l-2 border-primary/30 pl-6">
                  If the knowledge is lost, the asset is lost.
                </p>
              </div>
            </div>

            {/* The real problem */}
            <div className="space-y-6">
              <h2 className="text-2xl font-serif text-foreground">
                The absence of orientation
              </h2>
              <p className="text-muted-foreground leading-[1.9]">
                What made the situation harder wasn't just the assets. It was the absence of orientation.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <p className="text-muted-foreground leading-[1.8]">We didn't need perfect records.</p>
                  <p className="text-muted-foreground leading-[1.8]">We didn't need transaction histories.</p>
                </div>
                <div>
                  <p className="text-foreground leading-[1.8] font-medium">We needed context.</p>
                </div>
              </div>
              <div className="bg-secondary/30 rounded-xl p-6 space-y-2">
                <p className="text-foreground/80">What exists?</p>
                <p className="text-foreground/80">What matters?</p>
                <p className="text-foreground/80">What doesn't matter?</p>
                <p className="text-foreground/80">Where is access managed?</p>
                <p className="text-foreground/80">Who is this intended for?</p>
              </div>
              <p className="text-muted-foreground leading-[1.9] italic">
                Instead, we were left guessing.
              </p>
            </div>

            {/* The cost */}
            <div className="space-y-6">
              <h2 className="text-2xl font-serif text-foreground">
                The cost of uncertainty
              </h2>
              <p className="text-muted-foreground leading-[1.9]">
                Navigating an estate without a will — and without visibility — is not just complex. It is emotionally exhausting. In our case, it also exposed us to legal environments where incentives were not always aligned. In the absence of clarity, decisions become expensive.
              </p>
              <p className="text-muted-foreground leading-[1.9]">
                We watched a significant portion of the estate disappear into professional fees simply because we didn't know where to start.
              </p>
            </div>

            {/* Closing */}
            <div className="space-y-6 pt-6 border-t border-border/50">
              <p className="text-foreground leading-[1.9] text-lg">
                We would not wish that experience on anyone.
              </p>
              <p className="text-foreground leading-[1.9] text-xl font-serif">
                That is why Lifely exists.
              </p>
            </div>

            {/* The Goal */}
            <div className="space-y-8 pt-12 mt-6 border-t border-border/50">
              <h2 className="text-2xl font-serif text-foreground">
                The Goal
              </h2>
              <p className="text-muted-foreground leading-[1.9]">
                Lifely exists so that:
              </p>
              <div className="bg-secondary/40 rounded-2xl p-8 space-y-4">
                <p className="text-foreground leading-[1.8]">Important assets are not permanently lost through lack of knowledge.</p>
                <p className="text-foreground leading-[1.8]">Families are not left guessing.</p>
                <p className="text-foreground leading-[1.8]">Stress is reduced, not amplified.</p>
                <p className="text-foreground leading-[1.8]">Context is preserved.</p>
                <p className="text-foreground leading-[1.8]">Intent survives change.</p>
                <p className="text-foreground leading-[1.8]">Complexity becomes manageable.</p>
              </div>
              <div className="space-y-4 pt-4">
                <p className="text-muted-foreground leading-[1.9] italic">
                  It was built from experience.
                </p>
                <p className="text-foreground leading-[1.9] font-medium">
                  And it was built so others don't have to learn the same lessons the hard way.
                </p>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 bg-foreground rounded-2xl p-8 md:p-12 text-center"
          >
            <h3 className="font-serif text-2xl md:text-3xl text-background">
              Ready to map what matters?
            </h3>
            <p className="mt-3 text-background/60 max-w-md mx-auto">
              Start with free Insights, or begin mapping straight away.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-4">
              <Link to="/pricing">
                <Button className="rounded-full bg-background text-foreground hover:bg-background/90 h-12 px-8 font-medium">
                  View pricing <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  variant="outline"
                  className="rounded-full border-background/30 text-background hover:bg-background/10 h-12 px-8 font-medium"
                >
                  Learn about Lifely
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
